/**
 * useVoiceRecorder — MediaRecorder-backed mic capture hook (native browser
 * API, no new dependency). Produces a single audio Blob per recording,
 * suitable for `transcribe()` (src/lib/api/voice.ts).
 *
 * Lifecycle: idle -> requesting (getUserMedia prompt) -> recording -> stopped
 * (resolves the Blob) | permission-denied | unsupported.
 *
 * Auto-stops at `maxDurationSec` (default 60s) so a forgotten recording
 * can't grow past the backend's upload-size gate.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useT } from '@/i18n';

export type VoiceRecorderStatus =
  | 'idle'
  | 'requesting'
  | 'recording'
  | 'stopped'
  | 'permission-denied'
  | 'unsupported';

export interface VoiceRecording {
  blob: Blob;
  mimeType: string;
  durationSec: number;
}

export interface UseVoiceRecorderOptions {
  /** Hard ceiling in seconds — auto-stops the recording. Default 60. */
  maxDurationSec?: number;
  /** Fired when the recorder auto-stops after hitting `maxDurationSec`. */
  onMaxDurationReached?: () => void;
}

export interface UseVoiceRecorderResult {
  status: VoiceRecorderStatus;
  /** Elapsed recording time in seconds, ticking while `status === 'recording'`. */
  elapsedSec: number;
  /** Last French, user-facing error (permission denied, unsupported browser…). */
  error: string | null;
  /** Requests the mic and starts recording. Resolves once recording has started (or failed). */
  start: () => Promise<void>;
  /** Stops recording and resolves with the captured audio, or `null` if nothing usable was captured. */
  stop: () => Promise<VoiceRecording | null>;
  /** Aborts the current recording without producing a Blob (discards audio). */
  cancel: () => void;
}

// Preference order: opus-in-webm is the smallest/best-supported codec for
// speech; ogg/opus and mp4 (Safari) are fallbacks. The backend accepts all of
// these (ACCEPTED_AUDIO_TYPES in voice_api.py).
const CANDIDATE_MIME_TYPES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/ogg;codecs=opus',
  'audio/ogg',
  'audio/mp4',
];

function pickSupportedMimeType(): string | undefined {
  if (typeof MediaRecorder === 'undefined' || typeof MediaRecorder.isTypeSupported !== 'function') {
    return undefined;
  }
  return CANDIDATE_MIME_TYPES.find((t) => {
    try {
      return MediaRecorder.isTypeSupported(t);
    } catch {
      return false;
    }
  });
}

function isMediaCaptureSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    typeof navigator.mediaDevices?.getUserMedia === 'function' &&
    typeof MediaRecorder !== 'undefined'
  );
}

export function useVoiceRecorder(options: UseVoiceRecorderOptions = {}): UseVoiceRecorderResult {
  const { t: tx } = useT();
  const maxDurationSec = options.maxDurationSec ?? 60;

  const [status, setStatus] = useState<VoiceRecorderStatus>(
    isMediaCaptureSupported() ? 'idle' : 'unsupported',
  );
  const [elapsedSec, setElapsedSec] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Latest callback without forcing `start` to be re-created every render.
  const onMaxDurationReachedRef = useRef(options.onMaxDurationReached);
  useEffect(() => {
    onMaxDurationReachedRef.current = options.onMaxDurationReached;
  }, [options.onMaxDurationReached]);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const mimeTypeRef = useRef<string>('');
  const startedAtRef = useRef<number>(0);
  const tickIntervalRef = useRef<number | undefined>(undefined);
  const maxDurationTimerRef = useRef<number | undefined>(undefined);
  const stopResolverRef = useRef<((r: VoiceRecording | null) => void) | null>(null);

  const clearTimers = useCallback(() => {
    if (tickIntervalRef.current !== undefined) {
      window.clearInterval(tickIntervalRef.current);
      tickIntervalRef.current = undefined;
    }
    if (maxDurationTimerRef.current !== undefined) {
      window.clearTimeout(maxDurationTimerRef.current);
      maxDurationTimerRef.current = undefined;
    }
  }, []);

  const releaseStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  const teardown = useCallback(() => {
    clearTimers();
    releaseStream();
    mediaRecorderRef.current = null;
    chunksRef.current = [];
  }, [clearTimers, releaseStream]);

  // Release the mic + timers if the component unmounts mid-recording.
  useEffect(() => teardown, [teardown]);

  const start = useCallback(async () => {
    setError(null);

    if (!isMediaCaptureSupported()) {
      setStatus('unsupported');
      setError(tx('shell.features.copilotUseVoiceRecorder.audioRecordingIsNotSupportedBy'));
      return;
    }

    setStatus('requesting');

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      setStatus('permission-denied');
      setError(
        tx('shell.features.copilotUseVoiceRecorder.microphoneAccessDeniedAllowTheMicrophone'),
      );
      return;
    }
    streamRef.current = stream;

    const mimeType = pickSupportedMimeType();
    mimeTypeRef.current = mimeType ?? '';

    let recorder: MediaRecorder;
    try {
      recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
    } catch {
      releaseStream();
      setStatus('unsupported');
      setError(tx('shell.features.copilotUseVoiceRecorder.couldNotInitializeTheAudioRecorder'));
      return;
    }
    mediaRecorderRef.current = recorder;
    chunksRef.current = [];

    recorder.ondataavailable = (event: BlobEvent) => {
      if (event.data && event.data.size > 0) chunksRef.current.push(event.data);
    };
    recorder.onerror = () => {
      setError(tx('shell.features.copilotUseVoiceRecorder.anErrorOccurredDuringAudioRecording'));
    };
    recorder.onstop = () => {
      const durationSec = (Date.now() - startedAtRef.current) / 1000;
      const blob = new Blob(chunksRef.current, {
        type: recorder.mimeType || mimeTypeRef.current || 'audio/webm',
      });
      chunksRef.current = [];
      clearTimers();
      releaseStream();
      setStatus('stopped');

      const resolve = stopResolverRef.current;
      stopResolverRef.current = null;
      resolve?.(blob.size > 0 ? { blob, mimeType: blob.type, durationSec } : null);
    };

    startedAtRef.current = Date.now();
    setElapsedSec(0);
    recorder.start();
    setStatus('recording');

    tickIntervalRef.current = window.setInterval(() => {
      setElapsedSec((Date.now() - startedAtRef.current) / 1000);
    }, 250);

    maxDurationTimerRef.current = window.setTimeout(() => {
      onMaxDurationReachedRef.current?.();
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
    }, maxDurationSec * 1000);
  }, [clearTimers, maxDurationSec, releaseStream]);

  const stop = useCallback((): Promise<VoiceRecording | null> => {
    return new Promise((resolve) => {
      const recorder = mediaRecorderRef.current;
      if (!recorder || recorder.state === 'inactive') {
        resolve(null);
        return;
      }
      stopResolverRef.current = resolve;
      recorder.stop();
    });
  }, []);

  const cancel = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    stopResolverRef.current = null;
    if (recorder && recorder.state !== 'inactive') {
      // Discard — do not resolve a pending stop() promise with this stop.
      recorder.onstop = null;
      recorder.stop();
    }
    teardown();
    setStatus('idle');
    setElapsedSec(0);
  }, [teardown]);

  return { status, elapsedSec, error, start, stop, cancel };
}
